import { useEffect, useRef, useState } from "react";
import { Box, Skeleton, Tooltip, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FONT, WEIGHT } from "../../styles/foundation/typography";
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, withOpacity } from "../../styles/foundation/colors";

// Building blocks for the merchant panel's Help & Support (EMS V2) pages: the admin panel's
// interaction patterns (clickable tiles, lift on hover, staggered entrance), in this panel's own
// blue palette rather than the admin's purple.

/** One colour per tile: a solid for text and marks, a soft tint for the active state, a gradient
 *  for the icon tile and the bar along the top of the card. */
export const ACCENTS = {
    blue: { solid: PRIMARY_BLUE, soft: withOpacity(PRIMARY_BLUE, 0.08), grad: `linear-gradient(135deg, #5BA2F0 0%, ${PRIMARY_BLUE} 100%)` },
    navy: { solid: "#1E3A5F", soft: "#EDF2F8", grad: "linear-gradient(135deg, #3E6491 0%, #1E3A5F 100%)" },
    amber: { solid: "#B45309", soft: "#FEF5E7", grad: "linear-gradient(135deg, #F5A524 0%, #D97706 100%)" },
    green: { solid: "#0F8A5F", soft: "#E8F7F0", grad: "linear-gradient(135deg, #34C98F 0%, #0F8A5F 100%)" },
    teal: { solid: "#0E7C86", soft: "#E6F6F7", grad: "linear-gradient(135deg, #2EC4B6 0%, #0E7C86 100%)" },
    red: { solid: "#C62828", soft: "#FDECEC", grad: "linear-gradient(135deg, #EF6B6B 0%, #C62828 100%)" },
    grey: { solid: "#5F6B7A", soft: "#F1F3F6", grad: "linear-gradient(135deg, #A3ADBA 0%, #6B7685 100%)" },
};

export const CARD_SX = {
    bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: "14px",
    boxShadow: "0 1px 2px rgba(16,24,40,.04), 0 6px 20px rgba(30,58,95,.06)",
};

const reducedMotion = () => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; }
};

const riseKf = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; }`;
/** Fade-and-rise on first paint, staggered by order; off under reduced motion. */
export const rise = (order = 0) => ({
    animation: `${riseKf} 380ms cubic-bezier(.2,.7,.2,1) ${Math.min(order, 8) * 50}ms both`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

/** Lift on hover for clickable cards. */
export const LIFT = {
    transition: "box-shadow 180ms, transform 180ms, border-color 180ms, background-color 180ms",
    "&:hover": { boxShadow: "0 2px 4px rgba(16,24,40,.05), 0 12px 28px rgba(30,58,95,.12)", transform: "translateY(-2px)" },
    "@media (prefers-reduced-motion: reduce)": { "&:hover": { transform: "none" } },
};

/** Counts a number up to its value (300ms); straight to it under reduced motion. */
export const useCountUp = (target) => {
    const [shown, setShown] = useState(target);
    const from = useRef(0);
    useEffect(() => {
        if (typeof target !== "number" || reducedMotion()) { setShown(target); return undefined; }
        const start = performance.now();
        const begin = from.current;
        let raf;
        const step = (t) => {
            const k = Math.min(1, (t - start) / 300);
            setShown(Math.round(begin + (target - begin) * k));
            if (k < 1) raf = requestAnimationFrame(step);
            else from.current = target;
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target]);
    return shown;
};

/**
 * A headline number that is also a filter: clicking it shows those tickets in the list below,
 * a second click clears it. value is a number (counted up) or a ready string.
 */
export const KpiTile = ({ label, value, sub, info, icon: Icon, accent = ACCENTS.blue, active, loading, onClick, order = 0 }) => {
    const animated = useCountUp(typeof value === "number" ? value : null);
    const shown = typeof value === "number" ? (animated ?? value).toLocaleString("en-IN") : value;
    return (
        <Box component="button" type="button" onClick={onClick} aria-pressed={!!active}
            aria-label={`${label}: ${loading ? "loading" : shown}. ${active ? "Showing these tickets, click to clear" : "Show these tickets"}`}
            sx={{
                all: "unset", boxSizing: "border-box", cursor: "pointer", ...CARD_SX, ...LIFT, ...rise(order),
                position: "relative", overflow: "hidden", minWidth: 0, p: { xs: 1.5, md: 1.75 }, pt: { xs: 1.75, md: 2 },
                display: "flex", flexDirection: "column", gap: 0.75,
                borderColor: active ? accent.solid : BORDER, bgcolor: active ? accent.soft : WHITE,
                boxShadow: active ? `0 0 0 1px ${accent.solid}, 0 8px 22px ${withOpacity(accent.solid, 0.18)}` : CARD_SX.boxShadow,
                "&::before": { content: '""', position: "absolute", left: 0, right: 0, top: 0, height: 3, background: accent.grad },
                "&:focus-visible": { outline: `2px solid ${accent.solid}`, outlineOffset: 2 },
            }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box aria-hidden sx={{ width: 30, height: 30, borderRadius: "9px", background: accent.grad, display: "grid", placeItems: "center",
                    flexShrink: 0, boxShadow: `0 4px 10px ${withOpacity(accent.solid, 0.25)}`, "& svg": { fontSize: 17, color: WHITE } }}>
                    {Icon && <Icon />}
                </Box>
                {active ? <CheckCircleIcon sx={{ fontSize: 16, color: accent.solid }} aria-hidden /> : info && (
                    <Tooltip title={info} arrow placement="top">
                        <InfoOutlinedIcon onClick={(e) => e.stopPropagation()} sx={{ fontSize: 15, color: SUBTLE, opacity: 0.7, cursor: "help" }} />
                    </Tooltip>
                )}
            </Box>
            {/* Two lines' height for every label, so the numbers line up across the row. */}
            <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: WEIGHT.bold, letterSpacing: ".03em", textTransform: "uppercase",
                color: active ? accent.solid : SUBTLE, lineHeight: 1.35, minHeight: "2.7em",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {label}
            </Typography>
            {loading ? <Skeleton width={70} height={34} /> : (
                <Typography sx={{ fontFamily: FONT, fontSize: { xs: 22, md: 26 }, fontWeight: WEIGHT.extra, color: NAVY, lineHeight: 1.2,
                    fontVariantNumeric: "tabular-nums" }}>
                    {shown}
                </Typography>
            )}
            <Typography title={active ? undefined : sub} sx={{ fontFamily: FONT, fontSize: 12, color: active ? accent.solid : SUBTLE,
                fontWeight: active ? WEIGHT.bold : WEIGHT.medium, lineHeight: 1.4, minHeight: "2.8em",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {active ? "Showing these · click to clear" : sub}
            </Typography>
        </Box>
    );
};

/** Ticket status as a dot + label; waiting on the brand gets its own amber look. */
export const statusAccent = (row) => {
    if (row?.slaPausedAt && row?.statusCategory === "PENDING") return ACCENTS.amber;
    return { OPEN: ACCENTS.blue, PENDING: ACCENTS.navy, RESOLVED: ACCENTS.green, CLOSED: ACCENTS.grey }[row?.statusCategory] || ACCENTS.navy;
};

const pulseKf = keyframes`0% { box-shadow: 0 0 0 0 rgba(180,83,9,.45); } 70% { box-shadow: 0 0 0 6px rgba(180,83,9,0); } 100% { box-shadow: 0 0 0 0 rgba(180,83,9,0); }`;

export const StatusChip = ({ row }) => {
    const a = statusAccent(row);
    const waiting = row?.slaPausedAt && row?.statusCategory === "PENDING";
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.1, py: 0.4, borderRadius: "999px",
            bgcolor: a.soft, color: a.solid, fontFamily: FONT, fontSize: 12, fontWeight: WEIGHT.bold, lineHeight: 1.4, maxWidth: "100%" }}>
            <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: a.solid, flexShrink: 0,
                ...(waiting ? { boxShadow: `0 0 0 0 ${withOpacity(a.solid, 0.5)}`, animation: `${pulseKf} 1.8s ease-out infinite`,
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" } } : {}) }} />
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row?.status}</Box>
        </Box>
    );
};

/** Uppercase field label above a filter. */
export const FieldLabel = ({ children }) => (
    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
        {children}
    </Typography>
);
